// ╔══════════════════════════════════════════════════════════════╗
// ║  Production Workers — full management page                    ║
// ║  Search + filters + CRUD + Excel export/import + performance  ║
// ║  Admin sees view-only (no add/edit/delete)                    ║
// ╚══════════════════════════════════════════════════════════════╝

import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, downloadFile, getErrorMessage } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.jsx';
import { useToast } from '../../components/Toast.jsx';
import { fmtAR } from '../../lib/format.js';
import {
  Avatar,
  Badge,
  Btn,
  Card,
  Chip,
  Field,
  Input,
  Modal,
  Pills,
  Progress,
  Select,
} from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';

const PERIOD_LABEL = { day: 'اليوم', week: 'الأسبوع', month: 'الشهر' };

export default function ProductionWorkersPage() {
  const { user } = useAuth();
  // admin + eng can manage workers. acc views read-only.
  const canManage = user?.role === 'eng' || user?.role === 'admin';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const qc = useQueryClient();
  const fileInput = useRef(null);

  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage') ?? 'all');
  const [activeFilter, setActiveFilter] = useState('true');
  const [period, setPeriod] = useState('day'); // day | week | month
  const [editing, setEditing] = useState(null); // worker object or { new: true }
  const [deleting, setDeleting] = useState(null);

  const params = {
    isActive: activeFilter === 'all' ? undefined : activeFilter,
    stageId: stageFilter === 'all' ? undefined : stageFilter,
    search: search || undefined,
    period,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['production', 'workers', params],
    queryFn: () => api.get('/production/workers', { params }).then((r) => r.data),
    keepPreviousData: true,
  });

  const { data: stagesData } = useQuery({
    queryKey: ['production', 'stages'],
    queryFn: () => api.get('/production/stages').then((r) => r.data),
  });

  const workers = data?.workers ?? [];
  const stages = stagesData?.stages ?? [];
  const stageMap = Object.fromEntries(stages.map((s) => [s.id, s]));

  // ─ Mutations ─
  const saveMut = useMutation({
    mutationFn: (payload) => {
      if (editing?.id && !editing.new) {
        return api.patch(`/production/workers/${editing.id}`, payload);
      }
      return api.post('/production/workers', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production', 'workers'] });
      qc.invalidateQueries({ queryKey: ['production', 'stages'] });
      qc.invalidateQueries({ queryKey: ['reports', 'eng-dashboard'] });
      toast.push({
        tone: 'success',
        title: editing?.new ? 'تم إضافة العامل' : 'تم حفظ التعديلات',
      });
      setEditing(null);
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الحفظ', body: getErrorMessage(err) }),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/production/workers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production', 'workers'] });
      toast.push({ tone: 'success', title: 'تم تعطيل العامل' });
      setDeleting(null);
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل', body: getErrorMessage(err) }),
  });

  const importMut = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('/production/workers/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['production', 'workers'] });
      const s = res.data.summary;
      toast.push({
        tone: 'success',
        title: 'تم الاستيراد',
        body: `${s.inserted} عامل جديد · ${s.skipped} متجاهل`,
      });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الاستيراد', body: getErrorMessage(err) }),
  });

  function handleExport() {
    downloadFile(
      '/production/workers/export',
      `production_workers_${new Date().toISOString().slice(0, 10)}.csv`,
    ).catch((err) =>
      toast.push({ tone: 'danger', title: 'فشل التحميل', body: getErrorMessage(err) }),
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            عمال الإنتاج
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">
            إدارة العمال
            <span className="text-ink-400 font-bold mr-2 tabular">· {fmtAR(workers.length)}</span>
          </h1>
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <Btn variant="ghost" icon="download" onClick={handleExport}>
            تصدير Excel
          </Btn>
          {canManage && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) importMut.mutate(f);
                  e.target.value = '';
                }}
              />
              <Btn
                variant="outline"
                icon="upload"
                loading={importMut.isPending}
                onClick={() => fileInput.current?.click()}
              >
                رفع Excel
              </Btn>
              <Btn variant="primary" icon="plus" onClick={() => setEditing({ new: true })}>
                إضافة عامل
              </Btn>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="search" className="absolute top-3 right-3 text-ink-400" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث بالاسم أو رقم العامل…"
              className="w-full h-10 px-4 pe-10 rounded-xl bg-ink-50 dark:bg-ink-800 ring-1 ring-ink-200 dark:ring-ink-700 focus:ring-2 focus:ring-brand-400 outline-none text-sm"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <Chip active={stageFilter === 'all'} onClick={() => setStageFilter('all')}>
              كل المراحل
            </Chip>
            {stages.map((s) => (
              <Chip
                key={s.id}
                active={stageFilter === s.id}
                onClick={() => setStageFilter(s.id)}
              >
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.nameAr}
                </span>
              </Chip>
            ))}
          </div>
          <Pills
            value={activeFilter}
            onChange={setActiveFilter}
            items={[
              { value: 'true', label: 'نشط' },
              { value: 'false', label: 'غير نشط' },
              { value: 'all', label: 'الكل' },
            ]}
          />
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm flex-wrap">
          <span className="text-ink-400 text-xs">عرض الإنتاج لـ:</span>
          <Pills
            value={period}
            onChange={setPeriod}
            items={[
              { value: 'day', label: 'اليوم' },
              { value: 'week', label: 'الأسبوع' },
              { value: 'month', label: 'الشهر' },
            ]}
          />
          {data?.range && (
            <span className="text-[11px] text-ink-400 font-mono">
              {data.range.from === data.range.to
                ? data.range.from
                : `${data.range.from} → ${data.range.to}`}
            </span>
          )}
        </div>
      </Card>

      {/* Workers table — desktop */}
      <Card className="overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                {[
                  '#',
                  'العامل',
                  'المرحلة',
                  'الهدف اليومي',
                  `إنتاج ${PERIOD_LABEL[period]}`,
                  'نسبة التحقيق',
                  'الحالة',
                  '',
                ].map((h, i) => (
                  <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-soft">
              {isLoading && (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-ink-400">
                    جاري التحميل…
                  </td>
                </tr>
              )}
              {!isLoading && workers.length === 0 && (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-ink-400">
                    لا توجد نتائج
                  </td>
                </tr>
              )}
              {workers.map((w) => {
                const stage = stageMap[w.stageId];
                const produced = w.periodProduced ?? 0;
                const target = w.periodTarget ?? 0;
                // For period=day, target is just dailyTarget (1 day)
                const effTarget = period === 'day' && target === 0 ? w.dailyTarget : target;
                const eff = effTarget > 0 ? Math.round((produced / effTarget) * 100) : 0;
                return (
                  <tr key={w.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-400">{w.id}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={w.name} size={32} />
                        <div className="font-semibold">{w.name}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {stage && (
                        <span
                          className="inline-flex items-center gap-2 px-2 py-1 rounded-lg text-xs font-bold"
                          style={{ backgroundColor: `${stage.color}20`, color: stage.color }}
                        >
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                          {stage.nameAr}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular font-extrabold">
                      {fmtAR(w.dailyTarget)} <span className="text-[10px] text-ink-400">قطعة</span>
                    </td>
                    <td className="px-4 py-3 tabular">
                      <span className="font-bold">{fmtAR(produced)}</span>
                      <span className="text-ink-400 text-xs"> / {fmtAR(effTarget)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 w-32">
                        <Progress
                          value={Math.min(100, eff)}
                          tone={eff >= 100 ? 'success' : eff >= 80 ? 'brand' : 'warn'}
                        />
                        <span className="tabular text-xs font-semibold">{eff}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={w.isActive ? 'success' : 'neutral'} dot>
                        {w.isActive ? 'نشط' : 'معطّل'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => navigate(`/production/workers/${w.id}/monthly`)}
                          title="تقرير شهرى"
                          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-brand-100 dark:hover:bg-brand-950/30 text-brand-600"
                        >
                          <Icon name="cal" size={14} />
                        </button>
                        {canManage && (
                          <>
                            <button
                              onClick={() => setEditing(w)}
                              title="تعديل"
                              className="w-8 h-8 grid place-items-center rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800"
                            >
                              <Icon name="edit" size={14} />
                            </button>
                            {w.isActive && (
                              <button
                                onClick={() => setDeleting(w)}
                                title="تعطيل"
                                className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-100 dark:hover:bg-rose-950/30 text-rose-600"
                              >
                                <Icon name="trash" size={14} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="lg:hidden divide-soft">
          {workers.map((w) => {
            const stage = stageMap[w.stageId];
            const produced = w.periodProduced ?? 0;
            const target = w.periodTarget ?? 0;
            const effTarget = period === 'day' && target === 0 ? w.dailyTarget : target;
            const eff = effTarget > 0 ? Math.round((produced / effTarget) * 100) : 0;
            return (
              <div key={w.id} className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar name={w.name} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{w.name}</div>
                    <div className="text-[11px] text-ink-400 font-mono">{w.id}</div>
                  </div>
                  <Badge tone={w.isActive ? 'success' : 'neutral'} dot>
                    {w.isActive ? 'نشط' : 'معطّل'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                    <div className="text-ink-400">المرحلة</div>
                    <div className="font-bold">{stage?.nameAr ?? '—'}</div>
                  </div>
                  <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                    <div className="text-ink-400">الهدف</div>
                    <div className="font-bold tabular">{fmtAR(w.dailyTarget)}</div>
                  </div>
                  <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                    <div className="text-ink-400">إنتاج {PERIOD_LABEL[period]}</div>
                    <div className="font-bold tabular">
                      {fmtAR(produced)} / {fmtAR(effTarget)}
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                    <div className="text-ink-400">نسبة التحقيق</div>
                    <div className="font-bold tabular">{eff}%</div>
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-2 mt-2">
                    <Btn variant="outline" size="sm" icon="edit" onClick={() => setEditing(w)}>
                      تعديل
                    </Btn>
                    {w.isActive && (
                      <Btn variant="ghost" size="sm" icon="trash" onClick={() => setDeleting(w)}>
                        تعطيل
                      </Btn>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Add/Edit modal */}
      {editing && canManage && (
        <WorkerForm
          worker={editing.new ? null : editing}
          stages={stages}
          onClose={() => setEditing(null)}
          onSave={(p) => saveMut.mutate(p)}
          loading={saveMut.isPending}
        />
      )}

      {/* Delete confirm */}
      {deleting && canManage && (
        <Modal
          open
          onClose={() => setDeleting(null)}
          title="تعطيل عامل"
          size="sm"
          footer={
            <>
              <Btn variant="ghost" onClick={() => setDeleting(null)}>إلغاء</Btn>
              <Btn variant="danger" icon="trash" loading={delMut.isPending} onClick={() => delMut.mutate(deleting.id)}>
                تأكيد التعطيل
              </Btn>
            </>
          }
        >
          <p className="text-sm">
            هل تريد تعطيل <span className="font-bold">{deleting.name}</span>؟
          </p>
          <p className="text-xs text-ink-400 mt-2">
            هيتم تعطيله فقط (مش حذف). يقدر الـ admin يفعّله تاني لاحقاً.
            بياناته السابقة (الإدخالات، الرواتب) مش هتتغير.
          </p>
        </Modal>
      )}
    </div>
  );
}

function WorkerForm({ worker, stages, onClose, onSave, loading }) {
  const isEdit = !!worker;
  const [form, setForm] = useState({
    employeeId: worker?.employeeId ?? '',
    name: worker?.name ?? '',
    stageId: worker?.stageId ?? stages[0]?.id ?? '',
    dailyTarget: worker?.dailyTarget ?? '',
    workHoursPerDay: worker?.workHoursPerDay ?? 8,
    timePerPieceSeconds: worker?.timePerPieceSeconds ?? '',
  });
  const [lookupError, setLookupError] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // When the code field blurs (or changes after a short pause), look the
  // employee up and auto-fill the name. Skip in edit mode (name already set).
  async function lookupCode(code) {
    const c = code.trim();
    if (!c || isEdit) return;
    setLookupError(null);
    setLookingUp(true);
    try {
      const res = await api.get(`/employees/${encodeURIComponent(c)}`);
      const emp = res.data?.employee;
      if (emp) {
        setForm((f) => ({ ...f, name: emp.name }));
      }
    } catch (err) {
      setLookupError('لا يوجد موظف بهذا الكود');
      setForm((f) => ({ ...f, name: '' }));
    } finally {
      setLookingUp(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.employeeId.trim()) return;
    const payload = {
      employeeId: form.employeeId.trim(),
      name: form.name || undefined,
      stageId: form.stageId,
      dailyTarget: Number(form.dailyTarget),
      workHoursPerDay: form.workHoursPerDay ? Number(form.workHoursPerDay) : undefined,
      timePerPieceSeconds: form.timePerPieceSeconds ? Number(form.timePerPieceSeconds) : undefined,
    };
    onSave(payload);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `تعديل: ${worker.name}` : 'إضافة عامل جديد'}
      size="md"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
          <Btn type="submit" form="worker-form" variant="primary" icon="check" loading={loading}>
            {isEdit ? 'حفظ' : 'إضافة'}
          </Btn>
        </>
      }
    >
      <form id="worker-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="كود الموظف" hint="الكود من ملف الموارد البشرية — الاسم يظهر تلقائياً">
          <Input
            value={form.employeeId}
            onChange={(e) => {
              upd('employeeId')(e);
              setLookupError(null);
            }}
            onBlur={(e) => lookupCode(e.target.value)}
            required
            disabled={isEdit}
            dir="ltr"
            placeholder="مثال: 1 / 275 / 360"
          />
        </Field>
        <Field label="الاسم" hint={lookingUp ? 'جاري البحث…' : isEdit ? '' : 'يُملأ تلقائياً من الكود'}>
          <Input
            value={form.name}
            onChange={upd('name')}
            readOnly={!isEdit}
            placeholder={lookingUp ? '…' : 'سيظهر بعد إدخال الكود'}
            className={lookupError ? 'ring-rose-400' : ''}
          />
          {lookupError && (
            <div className="text-[11px] text-rose-500 mt-1">{lookupError}</div>
          )}
        </Field>
        <Field label="المرحلة">
          <Select value={form.stageId} onChange={upd('stageId')} required>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>{s.nameAr}</option>
            ))}
          </Select>
        </Field>
        <Field label="الهدف اليومي (قطعة)">
          <Input type="number" value={form.dailyTarget} onChange={upd('dailyTarget')} required min={1} />
        </Field>
        <Field label="ساعات العمل في اليوم" hint="عادة 8 ساعات">
          <Input
            type="number"
            value={form.workHoursPerDay}
            onChange={upd('workHoursPerDay')}
            min={1}
            max={24}
            step={1}
          />
        </Field>
        <Field label="زمن القطعة (ثانية)" hint="عدد الثوانى لإنتاج قطعة واحدة">
          <Input
            type="number"
            value={form.timePerPieceSeconds}
            onChange={upd('timePerPieceSeconds')}
            min={1}
            step={1}
          />
        </Field>
      </form>
    </Modal>
  );
}
